import { Box, CircularProgress } from '@mui/material';
import { config } from 'appConfig';
import { HomeLayout } from 'components/Layouts/HomeLayout';
import { ProtectedLayout } from 'components/Layouts/ProtectedLayout';
import { NotFound } from 'components/NotFound';
import { ProtectedRoutes } from 'components/routing/ProtectedRoutes';
import { usePostHog } from 'posthog-js/react';
import { FunctionComponent, lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

const LazyAuthPage = lazy(() => import('pages/Auth/AuthPage').then(({ AuthPage }) => ({ default: AuthPage })));

const LazyLogoutPage = lazy(() =>
  import('components/pages/Auth/LogoutPage').then(({ LogoutPage }) => ({ default: LogoutPage })),
);

const LazyManageFBPagesV2 = lazy(() =>
  import('pages/ManageFBPagesV2/ManageFBPagesV2').then(({ ManageFBPagesV2 }) => ({ default: ManageFBPagesV2 })),
);

const LazyManageFBPagesV3 = lazy(() =>
  import('pages/ManageFBPagesV3/ManageFBPagesV3').then(({ ManageFBPagesV3 }) => ({ default: ManageFBPagesV3 })),
);

const LazyLinkTikTokAccount = lazy(() =>
  import('pages/LinkTikTokAccount/LinkTikTokAccount').then(({ LinkTikTokAccount }) => ({
    default: LinkTikTokAccount,
  })),
);

export const MANAGE_FB_PAGE_PERMISSIONS_ROUTE_URL = 'manage-facebook-page-permissions/v2';
export const MANAGE_FB_PAGE_PERMISSIONS_ROUTE_URL_V2 = 'manage-facebook-page-permissions/v2';

// For when external login pages redirect to /login_success
const SocialLoginSuccess: FunctionComponent = () => {
  useEffect(() => {
    window.localStorage.setItem('loginSuccessFlag', 'true');

    window.close();
  }, []);

  return null;
};

const Loading: FunctionComponent = () => (
  <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100vh">
    <CircularProgress size="5rem" thickness={2.5} />
  </Box>
);

export const AppRouting: FunctionComponent = () => {
  const { pathname } = useLocation();
  const posthogClient = usePostHog();

  /**
   * This is a posthog event.
   *
   * @see {@link https://posthog.com/docs/integrate/client/js#one-page-apps-and-page-views}
   */
  useEffect(() => {
    if (config.posthogShouldTrack && posthogClient) {
      posthogClient.capture('$pageview', { url: pathname });
    }
  }, [pathname, posthogClient]);

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Non-authenticated user routes */}
        <Route element={<HomeLayout />}>
          <Route path="/" element={<Navigate to="/auth/login" />} />
          <Route path="/login_success" element={<SocialLoginSuccess />} />
          <Route path="/auth">
            <Route index path="*" element={<LazyAuthPage />} />
          </Route>
        </Route>
        {/* Authenticated-users only */}
        <Route element={<ProtectedLayout />}>
          <Route path="*" element={<ProtectedRoutes />} />
        </Route>
        <Route path="/auth/logout" element={<LazyLogoutPage />} />
        <Route path="/manage-facebook-page-permissions">
          <Route index path="*" element={<LazyManageFBPagesV2 />} />
        </Route>
        <Route path="/manage-facebook-page-permissions/v2">
          <Route index path="*" element={<LazyManageFBPagesV2 />} />
        </Route>
        <Route path="/manage-facebook-page-permissions/v3">
          <Route index path="*" element={<LazyManageFBPagesV3 />} />
        </Route>
        <Route path="/link-tiktok-account">
          <Route index path="*" element={<LazyLinkTikTokAccount />} />
        </Route>
        {/* Fallback: not found page */}
        <Route path="/not-found" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/not-found" replace />} />
      </Routes>
    </Suspense>
  );
};
